'use client';

import type { SxProps } from '@mui/material';
import { Box, LinearProgress } from '@mui/material';
import type { DtbPubl, SchoolYearKey } from '@ocode/domain';
import { SchoolYear } from '@ocode/domain';
import type { ApiRequestContext } from '@ocodelib/api-common';
import { flatSx, FlexRow, TopAbsolute } from '@ocodelib/mui';
import { useIsomorphicEffect, useLoadingVisible } from '@ocodelib/mui/hooks';
import { sxChildSpaceX, sxChildSpaceY } from '@repo-foundation/util/sx-util';
import { NoDataView } from '@repo-ui/components/NoDataView';
import { useApiEffect } from '@repo-ui/hooks/useApiEffect';
import { useApi } from '@repo-ui/provider/useApi';
import { routerPushI18n } from '@repo-ui/router';
import { handleError } from '@repo-ui/util/handle-error';
import clsx from 'clsx';
import { useLocale } from 'next-intl';
import { useCallback, useState } from 'react';
import type { LocalBookItem, LocalSectionItem } from '../types';
import { convertDtbItemToLocalItems } from '../util';
import { BookListRow } from './BookListRow';
import { SectionListRow } from './SectionListRow';

type Data = {
  dtb: DtbPubl;
  dtbItems: (LocalSectionItem | LocalBookItem)[];
  sectionItems: LocalSectionItem[];
};

interface Props {
  sx?: SxProps;
  className?: string;
  yearKey: SchoolYearKey;
  subjectKey: string;
}

export function DtbTocContainer(props: Props) {
  const { sx, className, subjectKey, yearKey } = props;
  const [loadingState, setLoadingState] = useState<'none' | 'loading' | 'done'>('none');
  const loadingVisible = useLoadingVisible(loadingState === 'loading');
  const api = useApi();
  const [data, setData] = useState<Data>();
  const locale = useLocale();

  useIsomorphicEffect(() => {
    setData(undefined);
  }, [subjectKey, yearKey]);

  const doLoad = useCallback(
    async (params: { ctx: ApiRequestContext; yearKey: SchoolYearKey; subjectKey: string }) => {
      setLoadingState('loading');
      try {
        const { dtb, dtbItems = [] } = await api.dtb.publishedDtbDetails(params);
        const allItems = convertDtbItemToLocalItems(dtbItems);
        const sections = allItems.filter((it) => it.type === 'SECTN');
        setData({
          dtb,
          dtbItems: allItems,
          sectionItems: sections,
        });
      } catch (err: any) {
        if (err['errorCode'] === 'E1_NO_SUCH_DATA') {
          setData(undefined);
        } else {
          handleError(err);
        }
      } finally {
        setLoadingState('done');
      }
    },
    [api],
  );

  useApiEffect(
    (ctx) => {
      if (!yearKey) return;
      doLoad({ ctx, yearKey, subjectKey });
    },
    [yearKey, subjectKey],
  );

  const handleClickRow = (item: LocalBookItem) => (event: React.MouseEvent) => {
    const dtbId = data?.dtb?.dtbId;
    if (!dtbId) return;

    const dtbKey = dtbId + '.' + item.bookId;
    routerPushI18n('/dtb/details', dtbKey);
  };

  const handleClickSectionRow = (event: React.MouseEvent, item: LocalBookItem) => {
    const dtbId = data?.dtb?.dtbId;
    if (!dtbId) return;

    const dtbKey = dtbId + '.' + item.bookId;
    routerPushI18n('/dtb/details', dtbKey);
  };

  const { dtb, dtbItems = [], sectionItems: sections = [] } = data ?? {};
  const yearKeys = dtb?.yearKeys ?? [];

  return (
    <Box
      className={clsx('DtbTocContainer-root', className)}
      sx={flatSx(
        {
          position: 'relative',
          minHeight: 640,
        },
        sx,
      )}
    >
      <NoDataView visible={loadingState === 'done' && dtbItems.length === 0} />

      {yearKeys.length > 0 && (
        <FlexRow
          sx={{
            ...sxChildSpaceX(0.5),
          }}
        >
          {yearKeys.map((y) => (
            <Box
              key={y}
              component="span"
              sx={{
                display: 'inline-block',
                lineHeight: 1,
                fontSize: '0.75rem',
                py: 0.5,
                px: 1,
                borderRadius: 1,
                color: '#fff',
                bgcolor: 'rgba(0, 0, 0, 0.7)',
              }}
            >
              {SchoolYear[y] ?? y}
            </Box>
          ))}
        </FlexRow>
      )}
      <Box
        sx={{
          position: 'relative',
          mt: 1,
          px: 0,
          pb: 5,
          ...sxChildSpaceY(3, '.SectionListRow-root'),
          ...sxChildSpaceY('10px', '.BookListRow-root'),
        }}
      >
        {dtbItems.map((item, i) => {
          if (item.type === 'BOOK') {
            return (
              <BookListRow
                item={item}
                key={item.bookId}
                seq={i + 1}
                onClickRow={handleClickRow(item)}
              />
            );
          }
          return (
            <SectionListRow
              item={item}
              key={item.sectionSn}
              seq={i + 1}
              onClickRow={handleClickSectionRow}
            />
          );
        })}
      </Box>

      {loadingVisible && (
        <TopAbsolute>
          <LinearProgress />
        </TopAbsolute>
      )}
    </Box>
  );
}
